import { Flex, HStack } from "@chakra-ui/react";
import {
  useConnectionState,
  useDisconnectButton,
  useTrackToggle,
} from "@livekit/components-react";
import { ConnectionState, DeviceUnsupportedError, Track } from "livekit-client";
import useDisplayToast from "modules/hooks/use-display-toast";
import Toast from "modules/toasts/toast";
import React, { useCallback, useState } from "react";
import {
  type DesktopCaptureSource,
  getDesktopScreenAccessStatus,
  isDesktopApp,
  selectDesktopScreenShareSource,
} from "./desktop-screen-share";
import {
  CallEndOutlinedIcon,
  MicFilledIcon,
  MicOffOutlinedIcon,
  ScreenShareOutlinedIcon,
  StopScreenShareOutlinedIcon,
  VideocamFilledIcon,
  VideocamOffOutlinedIcon,
} from "./video-call-bar-icons";
import { CallBarItem } from "./video-call-bar-item";
import { VideoCallScreenSharePicker } from "./video-call-screen-share-picker";

/** One-word labels under each control (match mobile). */
const CALL_BAR_LABELS = {
  audio: "Audio",
  video: "Video",
  share: "Share",
  stopShare: "Stop",
  end: "End",
} as const;

const SCREEN_SHARE_MESSAGES = {
  unsupported: "Screen sharing isn't supported in this browser.",
  failed: "Unable to share your screen. Please try again.",
  macDenied:
    "Screen Recording is turned off for Synco. Enable it in System Settings > Privacy & Security > Screen Recording, then try again.",
} as const;

/**
 * The browser throws `NotAllowedError` when the user dismisses the native picker (and
 * when the OS blocks capture). Neither warrants a toast: nothing started.
 */
function isScreenShareDismissed(error: Error): boolean {
  return error.name === "NotAllowedError" || error.name === "AbortError";
}

/**
 * LiveKit `buttonProps` include hook options (e.g. `stopTracks`) and `lk-*` classNames
 * that must not be forwarded to a native `<button>`.
 */
function withoutLiveKitButtonClassName(
  props: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    stopTracks?: boolean;
  },
): React.ButtonHTMLAttributes<HTMLButtonElement> {
  const { className: _className, stopTracks: _stopTracks, ...rest } = props;
  return rest;
}

export const VideoCallBottomBar: React.FC<{ endCall: () => void }> = ({
  endCall,
}) => {
  const { displayToast } = useDisplayToast();
  const connectionState = useConnectionState();
  const isDisconnected = connectionState === ConnectionState.Disconnected;
  const mic = useTrackToggle({ source: Track.Source.Microphone });
  const camera = useTrackToggle({ source: Track.Source.Camera });

  const onScreenShareError = useCallback(
    (error: Error) => {
      if (isScreenShareDismissed(error)) return;
      // eslint-disable-next-line no-console
      console.error("Screen share failed", error);
      const message =
        error instanceof DeviceUnsupportedError
          ? SCREEN_SHARE_MESSAGES.unsupported
          : SCREEN_SHARE_MESSAGES.failed;
      displayToast(() => <Toast message={message} />);
    },
    [displayToast],
  );

  // `audio: true` lets the browser's picker offer tab/system audio; the user opts in
  // there. In the desktop app the Electron handler supplies video only.
  const screenShare = useTrackToggle({
    source: Track.Source.ScreenShare,
    captureOptions: { audio: true, selfBrowserSurface: "exclude" },
    onDeviceError: onScreenShareError,
  });

  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const handleShareClick = useCallback(async () => {
    if (screenShare.enabled) {
      await screenShare.toggle(false);
      return;
    }
    if (!isDesktopApp()) {
      await screenShare.toggle(true);
      return;
    }
    const status = await getDesktopScreenAccessStatus().catch(
      () => "unknown" as const,
    );
    if (status === "denied" || status === "restricted") {
      displayToast(() => <Toast message={SCREEN_SHARE_MESSAGES.macDenied} />);
      return;
    }
    setIsPickerOpen(true);
  }, [displayToast, screenShare]);

  const handlePickSource = useCallback(
    async (source: DesktopCaptureSource) => {
      setIsPickerOpen(false);
      try {
        await selectDesktopScreenShareSource(source);
      } catch (error) {
        onScreenShareError(
          error instanceof Error ? error : new Error(String(error)),
        );
        return;
      }
      await screenShare.toggle(true);
    },
    [onScreenShareError, screenShare],
  );

  const closePicker = useCallback(() => setIsPickerOpen(false), []);

  const {
    buttonProps: {
      onClick: disconnectClick,
      disabled: disconnectDisabled,
      ...disconnectRest
    },
  } = useDisconnectButton({ stopTracks: true });

  const handleLeave = useCallback(() => {
    if (!isDisconnected) {
      disconnectClick();
    }
    endCall();
  }, [disconnectClick, endCall, isDisconnected]);

  return (
    <>
      <Flex
        className="lk-call-bottom-bar"
        w="full"
        justify="center"
        align="flex-start"
        flexShrink={0}
        py="26px"
        px="16px"
        bg="rgba(28, 28, 28, 0.92)"
        borderTop="1px solid rgba(255, 255, 255, 0.06)"
        borderTopLeftRadius="16px"
        borderTopRightRadius="16px"
      >
        <HStack
          spacing={{ base: "28px", md: "40px" }}
          align="flex-end"
          justify="center"
        >
          <CallBarItem
            label={CALL_BAR_LABELS.audio}
            variant="control"
            aria-label={mic.enabled ? "Mute microphone" : "Unmute microphone"}
            isOff={!mic.enabled}
            disabled={mic.pending}
            {...withoutLiveKitButtonClassName(mic.buttonProps)}
            icon={
              mic.enabled ? (
                <MicFilledIcon size={26} />
              ) : (
                <MicOffOutlinedIcon size={26} />
              )
            }
          />
          <CallBarItem
            label={CALL_BAR_LABELS.video}
            variant="control"
            aria-label={camera.enabled ? "Turn camera off" : "Turn camera on"}
            isOff={!camera.enabled}
            disabled={camera.pending}
            {...withoutLiveKitButtonClassName(camera.buttonProps)}
            icon={
              camera.enabled ? (
                <VideocamFilledIcon size={26} />
              ) : (
                <VideocamOffOutlinedIcon size={26} />
              )
            }
          />
          <CallBarItem
            label={
              screenShare.enabled
                ? CALL_BAR_LABELS.stopShare
                : CALL_BAR_LABELS.share
            }
            variant="control"
            aria-label={
              screenShare.enabled ? "Stop sharing screen" : "Share screen"
            }
            aria-pressed={screenShare.enabled}
            disabled={screenShare.pending || isDisconnected}
            onClick={handleShareClick}
            icon={
              screenShare.enabled ? (
                <StopScreenShareOutlinedIcon size={26} />
              ) : (
                <ScreenShareOutlinedIcon size={26} />
              )
            }
          />
          <CallBarItem
            label={CALL_BAR_LABELS.end}
            variant="end"
            aria-label="End call"
            {...withoutLiveKitButtonClassName(disconnectRest)}
            disabled={isDisconnected ? false : Boolean(disconnectDisabled)}
            onClick={handleLeave}
            icon={<CallEndOutlinedIcon size={26} />}
          />
        </HStack>
      </Flex>
      {isPickerOpen && (
        <VideoCallScreenSharePicker
          onSelect={handlePickSource}
          onCancel={closePicker}
        />
      )}
    </>
  );
};
